import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  RotateCw, Search, HelpCircle,
  CheckCircle2, Plus, ChevronLeft, ChevronRight,
  FileSearch, Trash2, Pencil, BookOpen, Layers,
  ChevronRight as ChevronRightIcon, Check, LayoutList, Trophy
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useListState } from "@/hooks/useListState";
import {
  useGetBankQuestionsQuery,
  useCreateBankQuestionMutation,
  useUpdateBankQuestionMutation,
  useDeleteBankQuestionMutation,
} from "@/redux/slices/questionBankApi";
import { useGetSubjectsQuery } from "@/redux/slices/subjectApi";
import { useGetChaptersQuery } from "@/redux/slices/chapterApi";
import { Question } from "@/redux/slices/testApi";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";

const questionSchema = z.object({
  text: z.string().min(5, "Question text too short"),
  image: z.string().optional().default(""),
  options: z.array(z.string().min(1, "Option cannot be empty")).length(4),
  correctAnswer: z.coerce.number().min(0).max(3),
  marks: z.coerce.number().min(1),
  explanation: z.string().optional().default(""),
  subjectId: z.string().min(1, "Subject required"),
  chapterId: z.string().min(1, "Chapter required"),
});
type QuestionFormValues = z.infer<typeof questionSchema>;

/** Safely parse options that may arrive as a JSON array, object, string, or already be an array */
function parseOptions(options: unknown): string[] {
  if (!options) return [];
  if (Array.isArray(options)) return options.map(String);

  let parsed = options;
  if (typeof options === "string") {
    try {
      parsed = JSON.parse(options);
      if (typeof parsed === "string") {
        try { parsed = JSON.parse(parsed); } catch {}
      }
    } catch {
      return [];
    }
  }

  if (Array.isArray(parsed)) {
    return parsed.map(String);
  }

  if (parsed && typeof parsed === "object") {
    const keys = Object.keys(parsed).sort();
    return keys.map((k) => String((parsed as Record<string, any>)[k]));
  }

  return [];
}

function isCorrectOption(i: number, correct: unknown): boolean {
  if (correct === i) return true;
  if (typeof correct === "string") {
    if (correct.toUpperCase() === String.fromCharCode(65 + i)) return true;
    if (parseInt(correct, 10) === i) return true;
  }
  return false;
}

export default function QuestionBankPage() {
  const list = useListState();
  const { data, isFetching, refetch } = useGetBankQuestionsQuery(list.queryParams());
  const { data: subjectsData } = useGetSubjectsQuery({ limit: 1000 });
  const { data: chaptersData } = useGetChaptersQuery({ limit: 1000 });
  const [createQuestion, { isLoading: isCreating }] = useCreateBankQuestionMutation();
  const [updateQuestion, { isLoading: isUpdating }] = useUpdateBankQuestionMutation();
  const [deleteQuestion, { isLoading: isDeleting }] = useDeleteBankQuestionMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Question | null>(null);
  const [toDelete, setToDelete] = useState<Question | null>(null);

  const form = useForm<QuestionFormValues>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      text: "", image: "", options: ["", "", "", ""],
      correctAnswer: 0, marks: 1, explanation: "",
      subjectId: "", chapterId: ""
    }
  });

  const watchSubject = form.watch("subjectId");

  const filteredChaptersForForm = useMemo(() => {
    if (!chaptersData?.data || !watchSubject) return [];
    return chaptersData.data.filter(c => c.subjectId === watchSubject);
  }, [chaptersData, watchSubject]);

  const filteredChapters = useMemo(() => {
    if (!chaptersData?.data || !list.filters.subjectId) return [];
    return chaptersData.data.filter(c => c.subjectId === list.filters.subjectId);
  }, [chaptersData, list.filters.subjectId]);

  const openCreate = () => {
    setEditing(null);
    form.reset({
      text: "", image: "", options: ["", "", "", ""],
      correctAnswer: 0, marks: 1, explanation: "",
      subjectId: list.filters.subjectId || "",
      chapterId: list.filters.chapterId || ""
    });
    setOpen(true);
  };

  const openEdit = (q: Question) => {
    setEditing(q);
    form.reset({
      text: q.text,
      image: q.image || "",
      options: parseOptions(q.options),
      correctAnswer: q.correctAnswer,
      marks: q.marks,
      explanation: q.explanation || "",
      subjectId: q.subjectId,
      chapterId: q.chapterId
    });
    setOpen(true);
  };

  const onSubmit = async (v: QuestionFormValues) => {
    try {
      if (editing) {
        await updateQuestion({ id: editing.id, body: v }).unwrap();
        toast.success("Question updated successfully");
      } else {
        await createQuestion(v).unwrap();
        toast.success("New question added to bank");
      }
      setOpen(false);
    } catch {
      toast.error("Failed to save question data");
    }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Question Bank"
        description="Centralized repository of MCQ assessments and practice questions"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> New Question
            </Button>
          </div>
        }
      />

      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar
            value={list.search}
            onChange={list.setSearch}
            placeholder="Search question bank..."
            className="flex-1"
          />

          <div className="flex flex-wrap items-center gap-3">
            <Select value={list.filters.subjectId || "all"} onValueChange={(v) => {
              list.setFilter("subjectId", v === "all" ? undefined : v);
              list.setFilter("chapterId", undefined);
            }}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjectsData?.data?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select
              value={list.filters.chapterId || "all"}
              onValueChange={(v) => list.setFilter("chapterId", v === "all" ? undefined : v)}
              disabled={!list.filters.subjectId}
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Chapters" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Chapters</SelectItem>
                {filteredChapters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isFetching ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 animate-pulse rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50" />)}
          </div>
        ) : (
          <div className="space-y-4">
            {data?.data?.map((q, idx) => (
              <Card key={q.id} className="group relative overflow-hidden bg-card/60 backdrop-blur-sm shadow-sm ring-1 ring-border hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
                <div className="absolute top-0 left-0 h-full w-1 bg-primary/10 transition-all group-hover:bg-primary" />

                <div className="flex flex-col md:flex-row">
                  <div className="flex-1 p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted font-mono text-[10px] font-bold text-muted-foreground shadow-inner">
                        #{(list.page - 1) * list.limit + idx + 1}
                      </div>
                      <div className="flex-1 space-y-4">
                        <div className="space-y-2">
                          <p className="text-sm font-bold leading-relaxed text-foreground/90">{q.text}</p>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-[10px] h-5 px-2 font-bold uppercase tracking-wider bg-primary/5 border-primary/10 text-primary">
                              {q.subject?.name}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] h-5 px-2 font-bold uppercase tracking-wider bg-muted text-muted-foreground">
                              {q.chapter?.name}
                            </Badge>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {parseOptions(q.options).map((opt, i) => {
                            const correct = isCorrectOption(i, q.correctAnswer);
                            return (
                              <div key={i} className={cn(
                                "flex items-center gap-3 p-2.5 px-3.5 rounded-xl border text-[11px] transition-all duration-300",
                                correct
                                  ? "bg-green-500/5 border-green-500/20 text-green-700 font-bold shadow-sm"
                                  : "bg-muted/30 border-transparent text-muted-foreground/80"
                              )}>
                                <span className={cn(
                                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-lg text-[9px] font-bold transition-colors",
                                  correct ? "bg-green-500 text-white" : "bg-black/5 text-muted-foreground"
                                )}>
                                  {String.fromCharCode(65 + i)}
                                </span>
                                <span className="truncate">{opt}</span>
                                {correct && <CheckCircle2 className="ml-auto h-3.5 w-3.5 text-green-500" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full md:w-36 border-t md:border-t-0 md:border-l bg-muted/5 p-5 flex flex-row md:flex-col justify-center items-center gap-4">
                    <div className="flex gap-2">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(q)} className="h-9 w-9 rounded-full hover:bg-primary/10 hover:text-primary transition-colors">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setToDelete(q)} className="h-9 w-9 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Separator className="hidden md:block w-8" />
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Weight</span>
                      <span className="text-sm font-black text-foreground">{q.marks} Pts</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isFetching && data && data.total > 0 && (
          <div className="flex flex-col items-center justify-between gap-6 border-t pt-8 sm:flex-row pb-12">
            <div className="flex items-center gap-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <span>Showing {data.data.length} of {data.total} Questions</span>
              <Select value={String(list.limit)} onValueChange={(v) => list.setLimit(Number(v))}>
                <SelectTrigger className="h-9 w-28 bg-card shadow-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 20, 50].map(n => <SelectItem key={n} value={String(n)}>{n} Per Page</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-xl shadow-sm" disabled={list.page === 1} onClick={() => list.setPage(list.page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1.5">
                {Array.from({ length: Math.ceil(data.total / list.limit) }, (_, i) => i + 1).slice(0, 5).map(p => (
                  <Button
                    key={p}
                    variant={list.page === p ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "h-9 w-9 rounded-xl text-xs font-bold transition-all shadow-sm",
                      list.page === p ? "shadow-lg shadow-primary/20" : "bg-card border-border/50"
                    )}
                    onClick={() => list.setPage(p)}
                  >
                    {p}
                  </Button>
                ))}
              </div>
              <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-xl shadow-sm" disabled={list.page >= Math.ceil(data.total / list.limit)} onClick={() => list.setPage(list.page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isFetching && data?.total === 0 && (
          <div className="flex flex-col items-center justify-center py-24 rounded-3xl border-2 border-dashed bg-card/50 backdrop-blur-sm border-primary/10 transition-all">
            <div className="p-6 rounded-3xl bg-primary/5 mb-6 ring-1 ring-primary/10">
              <FileSearch className="h-12 w-12 text-primary/40" />
            </div>
            <h3 className="font-black text-xl text-foreground/80 tracking-tight">No Questions Found</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-xs text-center leading-relaxed">We couldn't find any questions matching your filters. Try adjusting your search or subject selections.</p>
            <Button variant="link" onClick={() => { list.setSearch(""); list.setFilter("subjectId", undefined); }} className="mt-4 text-primary font-bold">
              Clear all filters
            </Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl flex flex-col p-0 overflow-hidden max-h-[95vh] border-none shadow-2xl rounded-2xl">
          <DialogHeader className="p-8 pb-4 bg-primary/5">
            <DialogTitle className="text-2xl font-bold">{editing ? "Refine Question" : "New MCQ Item"}</DialogTitle>
            <DialogDescription className="text-sm font-medium text-muted-foreground">Draft your question content and configure multiple-choice options.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-8 py-4 space-y-8">
            <form id="bank-question-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Subject</Label>
                  <Select value={form.watch("subjectId")} onValueChange={(v) => { form.setValue("subjectId", v); form.setValue("chapterId", ""); }}>
                    <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Subject" /></SelectTrigger>
                    <SelectContent>{subjectsData?.data?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Chapter</Label>
                  <Select value={form.watch("chapterId")} onValueChange={(v) => form.setValue("chapterId", v)} disabled={!watchSubject}>
                    <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Chapter" /></SelectTrigger>
                    <SelectContent>{filteredChaptersForForm.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-semibold text-foreground">Question Text</Label>
                <Textarea placeholder="Enter the MCQ question here..." className="resize-none shadow-sm h-28 font-medium leading-relaxed" {...form.register("text")} />
                {form.formState.errors.text && <p className="text-xs font-bold text-destructive">{form.formState.errors.text.message}</p>}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7 space-y-4">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <LayoutList className="h-4 w-4 text-primary" /> Define Options
                  </Label>
                  <div className="space-y-3">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border font-black text-xs transition-all shadow-inner",
                          form.watch("correctAnswer") === i ? "bg-green-500 border-green-500 text-white shadow-green-500/20" : "bg-muted text-muted-foreground"
                        )}>
                          {String.fromCharCode(65 + i)}
                        </div>
                        <Input
                          className="h-11 shadow-sm"
                          placeholder={`Option ${String.fromCharCode(65 + i)} distractor...`}
                          {...form.register(`options.${i}` as any)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-5 space-y-6">
                  <div className="space-y-4 p-6 rounded-2xl border bg-muted/20 shadow-inner">
                    <Label className="text-[10px] font-black text-primary uppercase tracking-widest">Mark Key Correct Answer</Label>
                    <RadioGroup
                      value={String(form.watch("correctAnswer"))}
                      onValueChange={(v) => form.setValue("correctAnswer", parseInt(v))}
                      className="grid grid-cols-2 gap-3"
                    >
                      {[0, 1, 2, 3].map(i => (
                        <div key={i} className={cn(
                          "flex items-center gap-2 rounded-xl border p-3 cursor-pointer bg-background hover:bg-muted/50 transition-all",
                          form.watch("correctAnswer") === i && "border-green-500 ring-1 ring-green-500/20 shadow-lg shadow-green-500/5 bg-green-50/50"
                        )} onClick={() => form.setValue("correctAnswer", i)}>
                          <RadioGroupItem value={String(i)} id={`opt-${i}`} className="sr-only" />
                          <Label htmlFor={`opt-${i}`} className="cursor-pointer font-bold text-xs uppercase">Option {String.fromCharCode(65 + i)}</Label>
                          {form.watch("correctAnswer") === i && <Check className="ml-auto h-3 w-3 text-green-500" />}
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="space-y-3">
                    <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Trophy className="h-4 w-4 text-yellow-500" /> Scoring Weight
                    </Label>
                    <Input type="number" className="h-11 shadow-sm font-mono text-base" {...form.register("marks")} />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-semibold text-foreground">Answer Explanation / Rationale</Label>
                <Textarea placeholder="Clarify why the correct option is the most accurate..." className="resize-none shadow-sm h-24 italic" {...form.register("explanation")} />
              </div>
            </form>
          </div>

          <DialogFooter className="p-8 border-t bg-muted/10">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-8 font-bold uppercase tracking-tight text-xs">Cancel</Button>
            <Button form="bank-question-form" type="submit" disabled={isCreating || isUpdating} className="h-11 px-8 rounded-xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-primary/20 group">
              {isCreating || isUpdating ? "Saving..." : editing ? "Update Question" : "Save Question"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => { if (toDelete) { await deleteQuestion(toDelete.id).unwrap(); toast.success("Question purged successfully"); setToDelete(null); } }}
        loading={isDeleting}
        title="Purge MCQ Item?"
        description="This will permanently remove the question from the assessment database."
      />
    </div>
  );
}
